import { useEffect, useState, useRef, useMemo, useLayoutEffect, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { AlertOctagon, Zap, Activity, Wifi, Clock, MapPin, Camera, MonitorPlay, Waves } from 'lucide-react';
import { SEVERITY_COLORS } from '@/components/SeverityChip';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

/**
 * NOC Overview Screen - kiosk-friendly, dark, auto-refreshing,
 * NEVER times out (session refreshed by heartbeat + keepalive).
 */

// Isolated + memoized so the parent's 1s clock re-render can't interrupt the
// marquee animation (that caused visible stutter). Only re-renders when the
// closure list actually changes. Measures its own width to hold a constant,
// readable ~90 px/s scroll speed regardless of how many closures there are.
const CriticalTicker = memo(function CriticalTicker({ items }) {
  const ref = useRef(null);
  const [duration, setDuration] = useState(80);
  let filled = [...items];
  while (filled.length < 12) filled = [...filled, ...items];
  const key = items.join('|');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const distance = el.scrollWidth / 2;
    setDuration(Math.max(20, Math.round(distance / 90)));
  }, [key]);
  return (
    <div ref={ref} className="ticker text-lg font-semibold" style={{ animationDuration: `${duration}s` }} data-testid="noc-ticker">
      {[...filled, ...filled].map((t, i) => (
        <span key={i} className="mx-8 whitespace-nowrap">{t}</span>
      ))}
    </div>
  );
});

export default function Noc() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(dayjs());
  const [cameras, setCameras] = useState([]);
  const [showCams, setShowCams] = useState(false);
  const [vms, setVms] = useState([]);
  const [showVms, setShowVms] = useState(false);
  const [quakes, setQuakes] = useState([]);
  const nav = useNavigate();

  const addToWall = (cam) => {
    try {
      const cur = JSON.parse(localStorage.getItem('ptt_cctv_selected') || '[]');
      if (cur.includes(cam.id)) { toast('Already on the CCTV wall'); return; }
      localStorage.setItem('ptt_cctv_selected', JSON.stringify([...cur, cam.id]));
      toast.success(`${cam.name} added to CCTV wall`, {
        action: { label: 'Open wall', onClick: () => nav('/noc/cameras') },
      });
    } catch { /* noop */ }
  };

  const load = async () => {
    try {
      const [a, s] = await Promise.all([
        api.get('/alerts'),
        api.get('/alerts/stats'),
      ]);
      setAlerts(a.data);
      setStats(s.data);
      setHealth({ ingest: s.data.ingest, feeds: s.data.feeds });
      setTick((x) => x + 1);
    } catch (e) { /* silent */ }
  };

  // Auto-refresh + heartbeat token refresh
  useEffect(() => {
    load();
    const dataIv = setInterval(load, 20000);         // data every 20s
    const clockIv = setInterval(() => setNow(dayjs()), 1000);
    // token refresh heartbeat every 20 minutes to prevent timeout
    const heartbeatIv = setInterval(() => api.get('/auth/me').catch(() => {}), 20 * 60 * 1000);
    // wake lock (best-effort, keeps screen active in Chrome kiosks)
    let wakeLock = null;
    (async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch { /* noop */ }
    })();
    return () => {
      clearInterval(dataIv); clearInterval(clockIv); clearInterval(heartbeatIv);
      if (wakeLock) wakeLock.release?.();
    };
  }, []);

  // camera catalogue for the "quick-add to CCTV wall" map layer (business/admin only)
  useEffect(() => {
    api.get('/cameras').then((r) => setCameras(r.data || [])).catch(() => {});
    api.get('/vms?active_only=true').then((r) => setVms(r.data || [])).catch(() => {});
    api.get('/quakes?limit=8').then((r) => setQuakes(r.data || [])).catch(() => {});
  }, []);

  const activeAlerts = useMemo(() => alerts.filter((a) => (a.status || '').toLowerCase() !== 'resolved'), [alerts]);
  const critical = useMemo(() => activeAlerts.filter((a) => a.severity === 'red'), [activeAlerts]);
  const urgent = useMemo(() => activeAlerts.filter((a) => a.severity === 'orange'), [activeAlerts]);
  const info = useMemo(() => activeAlerts.filter((a) => a.severity === 'yellow'), [activeAlerts]);

  const baseTicker = useMemo(() => {
    const items = critical.map((a) => `🔴 ${a.highway || 'NZ'} · ${a.location_area}`);
    if (items.length === 0) items.push('No critical closures active — network status nominal.');
    return items;
  }, [critical]);

  return (
    <div className="noc min-h-screen grain">
      {/* Header bar */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <img src="/brand/logo-icon@2x.png" alt="PTT" className="w-11 h-11 rounded-lg" />
          <div>
            <div className="font-display font-black text-2xl leading-none tracking-tight">PTT NZ ALERTS · NOC</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-teal-400 mt-1">Network Operations Centre — NZ State Highways</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/noc/cameras" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 transition text-sm font-semibold" data-testid="noc-to-cctv">
            <Camera className="w-4 h-4" /> CCTV Wall
          </Link>
          <Link to="/noc/vms" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm font-semibold" style={{ background: 'rgba(255,176,0,0.12)', color: '#ffb000' }} data-testid="noc-to-vms">
            <MonitorPlay className="w-4 h-4" /> VMS Wall
          </Link>
          <FeedStatus health={health} />
          <div className="text-right">
            <div className="font-display font-black text-4xl leading-none tabular-nums">{now.format('HH:mm:ss')}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mt-1">{now.format('ddd D MMM YYYY · NZDT')}</div>
          </div>
        </div>
      </div>

      {/* Critical closure banner */}
      {critical.length > 0 && (
        <div className="bg-red-600/95 border-y border-red-800 py-3 px-8 flex items-center gap-4 pulse-red">
          <AlertOctagon className="w-7 h-7 flex-shrink-0" />
          <div className="font-display font-black text-2xl tracking-tight">
            {critical.length} CRITICAL {critical.length === 1 ? 'CLOSURE' : 'CLOSURES'} ACTIVE
          </div>
          <div className="flex-1 overflow-hidden">
            <CriticalTicker items={baseTicker} />
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-6 p-6" style={{ minHeight: 'calc(100vh - 120px)' }}>
        {/* Severity counters */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <CountCard color="#E11D48" count={critical.length} label="Critical (Red)" sub="Road closures & major incidents" icon={<AlertOctagon className="w-6 h-6" />} />
          <CountCard color="#F7941D" count={urgent.length} label="Urgent (Orange)" sub="Crashes, hazards, delays" icon={<Zap className="w-6 h-6" />} />
          <CountCard color="#FBBF24" count={info.length} label="Info (Yellow)" sub="Roadworks, weather, minor" icon={<Activity className="w-6 h-6" />} />
          <div className="noc-card p-6">
            <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">Network</div>
            <div className="grid grid-cols-2 gap-3">
              <MetricSmall value={stats?.highways_affected ?? '—'} label="Highways" />
              <MetricSmall value={stats?.active ?? '—'} label="Active" />
              <MetricSmall value={stats?.closures ?? '—'} label="Closures" />
              <MetricSmall value={activeAlerts.length} label="Events" />
            </div>
          </div>
          {quakes.length > 0 && (
            <div className="noc-card p-5" data-testid="noc-quakes">
              <div className="flex items-center gap-2 mb-3">
                <Waves className="w-4 h-4" style={{ color: '#a78bfa' }} />
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">Recent Quakes · GeoNet</div>
              </div>
              <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                {quakes.map((q) => {
                  const mag = q.magnitude || 0;
                  const col = mag >= 5 ? '#f87171' : mag >= 4 ? '#fb923c' : '#a78bfa';
                  return (
                    <div key={q.id} className="flex items-center gap-3 text-sm" data-testid={`noc-quake-${q.id}`}>
                      <span className="font-display font-black tabular-nums w-11 text-center rounded px-1 py-0.5" style={{ background: `${col}22`, color: col }}>M{mag.toFixed(1)}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-zinc-200 truncate leading-tight">{q.locality}</span>
                        <span className="block text-[11px] text-zinc-500">{dayjs(q.time).fromNow()} · {Math.round(q.depth || 0)} km deep</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="col-span-12 lg:col-span-6 noc-card overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">Live Map</div>
              <div className="font-display font-bold text-xl">Active Events · NZ</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCams((s) => !s)}
                data-testid="noc-map-cameras-toggle"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${showCams ? 'bg-teal-500 text-black' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`}
                title="Show NZTA cameras — click a pin to add it to the CCTV wall"
              >
                <Camera className="w-3.5 h-3.5" /> Cameras{cameras.length ? ` (${cameras.length})` : ''}
              </button>
              <button
                onClick={() => setShowVms((s) => !s)}
                data-testid="noc-map-vms-toggle"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition"
                style={showVms ? { background: '#ffb000', color: '#111' } : { background: 'rgba(255,255,255,0.05)', color: '#d4d4d8' }}
                title="Show live VMS message signs"
              >
                <MonitorPlay className="w-3.5 h-3.5" /> Signs{vms.length ? ` (${vms.length})` : ''}
              </button>
              <LiveDot />
            </div>
          </div>
          <div className="flex-1 min-h-[400px]">
            <MapContainer center={[-41.2, 172.5]} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap · CARTO" />
              {activeAlerts.filter((a) => a.lat && a.lng).map((a) => (
                <CircleMarker
                  key={a.id}
                  center={[a.lat, a.lng]}
                  radius={a.severity === 'red' ? 10 : a.severity === 'orange' ? 8 : 6}
                  pathOptions={{
                    color: SEVERITY_COLORS[a.severity],
                    fillColor: SEVERITY_COLORS[a.severity],
                    fillOpacity: 0.7,
                    weight: 2,
                  }}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{a.event_type}</div>
                      <div>{a.location_area}</div>
                      <div className="text-zinc-500 mt-1">{a.status}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              {showCams && cameras.filter((c) => c.lat && c.lng).map((c) => (
                <CircleMarker
                  key={`cam-${c.id}`}
                  center={[c.lat, c.lng]}
                  radius={5}
                  pathOptions={{ color: '#0d9488', fillColor: c.offline ? '#64748b' : '#2dd4bf', fillOpacity: 0.9, weight: 1.5 }}
                >
                  <Popup>
                    <div className="text-xs" style={{ minWidth: 160 }}>
                      <div className="font-bold">{c.highway ? `${c.highway} · ` : ''}{c.name}</div>
                      <div className="text-zinc-500">{[c.region, c.direction].filter(Boolean).join(' · ')}</div>
                      {c.offline && <div className="text-red-500 mt-1">Offline / maintenance</div>}
                      <button
                        onClick={() => addToWall(c)}
                        data-testid={`noc-cam-add-${c.id}`}
                        className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-teal-500 text-black font-semibold hover:bg-teal-400"
                      >
                        + Add to CCTV wall
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              {showVms && vms.filter((s) => s.lat && s.lng).map((s) => (
                <CircleMarker
                  key={`vms-${s.id}`}
                  center={[s.lat, s.lng]}
                  radius={6}
                  pathOptions={{ color: '#b45309', fillColor: '#ffb000', fillOpacity: 0.95, weight: 1.5 }}
                >
                  <Popup>
                    <div className="text-xs" style={{ minWidth: 180 }}>
                      <div className="font-bold">{s.highway ? `${s.highway} · ` : ''}{s.name}</div>
                      {s.direction && <div className="text-zinc-500">{s.direction}</div>}
                      <div className="mt-1 whitespace-pre-wrap font-mono font-bold" style={{ color: '#b45309' }}>{s.message}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Event stream */}
        <div className="col-span-12 lg:col-span-3 noc-card p-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">Event Stream</div>
              <div className="font-display font-bold text-xl">Latest Events</div>
            </div>
            <LiveDot />
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            {activeAlerts.slice(0, 40).map((a) => (
              <div key={a.id} className="border-l-4 pl-3 py-2 rounded-r-md" style={{ borderColor: SEVERITY_COLORS[a.severity], background: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-display font-black" style={{ color: SEVERITY_COLORS[a.severity] }}>{a.severity.toUpperCase()}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="font-semibold text-zinc-300">{a.highway || 'NZ'}</span>
                  <span className="text-zinc-600 ml-auto">{dayjs(a.updated_at || a.created_at).fromNow()}</span>
                </div>
                <div className="text-sm font-semibold text-white mt-1 leading-tight">{a.event_type}</div>
                <div className="text-xs text-zinc-400 truncate">{a.location_area}</div>
              </div>
            ))}
            {activeAlerts.length === 0 && <div className="text-zinc-500 text-sm p-6 text-center">No active events. Enjoy the quiet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CountCard({ color, count, label, sub, icon }) {
  return (
    <div className="noc-card p-6 relative overflow-hidden">
      <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: color }} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color }}>{label}</div>
          <div className="noc-big text-6xl mt-2" style={{ color }}>{count}</div>
          <div className="text-xs text-zinc-500 mt-2 max-w-[160px]">{sub}</div>
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}22`, color }}>{icon}</div>
      </div>
    </div>
  );
}

function MetricSmall({ value, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl text-teal-400">{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function LiveDot() {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-500" />
      </span>
      <span className="text-teal-400 font-semibold tracking-wider uppercase">Live</span>
    </div>
  );
}

function FeedStatus({ health }) {
  const feeds = health?.feeds;
  if (!feeds || feeds.length === 0) {
    const ok = health?.ingest?.status === 'ok';
    return (
      <div className="flex items-center gap-3 text-xs">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${ok ? 'bg-teal-500/10 text-teal-400' : 'bg-red-500/10 text-red-400'}`}>
          <Wifi className="w-3.5 h-3.5" />
          <span className="font-semibold tracking-wider uppercase">NZTA {ok ? 'Online' : 'Error'}</span>
        </div>
      </div>
    );
  }
  const allOk = feeds.every((f) => f.status === 'ok');
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="noc-feed-status">
      <Wifi className={`w-3.5 h-3.5 ${allOk ? 'text-teal-400' : 'text-amber-400'}`} />
      {feeds.map((f) => {
        const ok = f.status === 'ok';
        return (
          <span key={f.key} className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: ok ? 'rgba(45,212,191,0.1)' : 'rgba(248,113,113,0.12)' }} title={f.last_poll ? `${f.label}: ${f.status} · ${dayjs(f.last_poll).fromNow()}` : `${f.label}: ${f.status}`} data-testid={`feed-${f.key}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? 'bg-teal-400' : 'bg-red-400'}`} />
            <span className={`font-semibold ${ok ? 'text-teal-300' : 'text-red-300'}`}>{f.label}</span>
          </span>
        );
      })}
    </div>
  );
}
